import { initializeApp } from 'firebase/app'
import { getAuth } from 'firebase/auth'
import { collection, getFirestore, query, where } from 'firebase/firestore'
import { getFunctions } from 'firebase/functions'
import { getMessaging } from 'firebase/messaging'
import { getStorage } from 'firebase/storage'

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
}

export const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
// export const db = getFirestore(app, '(default)')
export const db = getFirestore(app, process.env.REACT_APP_ENV === 'production' ? '(default)' : 'live-alarm-database-stage')
export const storage = getStorage(app)
export const functions = getFunctions(app)
export const messaging = getMessaging(app)
export { firebaseConfig }

// Cloud functions are deployed once per environment, and the environment is
// the endpoint rather than a field in the request body.
export const ENV_SUFFIX =
  process.env.REACT_APP_ENV === 'production' ? 'P' : 'S'

/**
 * Headers for a call to an authenticated Cloud Function.
 * Returns null when there is no signed-in user, so callers can bail out
 * instead of sending a request that will be refused with a 401.
 */
export const authedJsonHeaders = async () => {
  const idToken = await auth.currentUser?.getIdToken()
  if (!idToken) return null
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`
  }
}

// Phase 4A namespaces custom claims per environment, because Firebase Auth is
// project-wide while the database is per-deployment. The Firestore rules read
// exactly this key, so anything that branches on permission must read it here
// too rather than inferring it from an organisation id — a hardcoded org id
// and the rules WILL drift, and the symptom is a permission-denied that looks
// like a rules bug.
export const ENV_CLAIM_KEY =
  process.env.REACT_APP_ENV === 'production' ? 'prod' : 'stage'

export const getEnvClaim = async () => {
  if (!auth.currentUser) return {}
  const { claims } = await auth.currentUser.getIdTokenResult()
  return claims[ENV_CLAIM_KEY] || {}
}

// Waits for a claim the server has just been asked to recompute.
//
// boundOrgIds is NOT read from Firestore by the rules — it is baked into the
// ID token (see loadBoundOrgIds in functions/utils.js). Writing a dispatch
// binding fires the syncDispatchBindingClaims trigger, but the token in this
// browser is unchanged until it is force-refreshed, and Firebase only does that
// on its own about once an hour. So creating a binding appeared to do nothing:
// the document was there, the other organisation still was not.
//
// Polls because the trigger is asynchronous — the write resolves before the
// claim is rebuilt, so one refresh usually lands too early. Gives up quietly
// rather than blocking the navigation: the claim still arrives, just later.
export const waitForEnvClaim = async (predicate, tries = 5) => {
  if (!auth.currentUser) return {}
  let claim = {}
  for (let i = 0; i < tries; i++) {
    await new Promise((resolve) => setTimeout(resolve, 500 * (i + 1)))
    const { claims } = await auth.currentUser.getIdTokenResult(true)
    claim = claims[ENV_CLAIM_KEY] || {}
    if (predicate(claim)) return claim
  }
  console.warn('waitForEnvClaim: claim did not catch up in time', claim)
  return claim
}

// The rules allow reading a user document only when it is your own, in your own
// organisation, or in one you are dispatch-bound to. An unfiltered
// `collection(db, 'users')` listen is therefore denied outright — the query has
// to name the organisations up front.
//
// Same type trap as the rules' orgIdOf(): organization.id is a NUMBER in
// Firestore while the claim carries strings, so every id is converted back.
export const orgScopedUsersQuery = async () => {
  const { orgId, boundOrgIds = [] } = await getEnvClaim()
  const ids = [...new Set([orgId, ...boundOrgIds])]
    .map(Number)
    .filter((id) => !Number.isNaN(id))
    // ponytail: `in` takes at most 30 values; widen to one listen per chunk if
    // an account is ever bound to more organisations than that.
    .slice(0, 30)

  if (!ids.length) return null
  return query(collection(db, 'users'), where('organization.id', 'in', ids))
}
